// Governing documents as structured text (ADR 0068): the Governance & Board
// Policy Manual, the Executive Director's administrative procedures, the
// bylaws, and the policies already adopted, each kept in Hub as sections
// (chapters, policies with their ids, procedures, articles and sections)
// rather than as a file. A manual has versions; one draft at a time is
// edited; any two versions compare as a redline.
//
// This module is self-contained (no imports): the load script
// (scripts/manual-drafts-load-sql.mjs) runs it under plain Node from the
// TypeScript source.

export const MANUAL_KINDS = ['board_policy', 'ed_procedure', 'bylaws'] as const
export type ManualKind = (typeof MANUAL_KINDS)[number]
export const MANUAL_KIND_LABELS: Record<ManualKind, string> = { board_policy: 'Board policy', ed_procedure: 'ED procedures', bylaws: 'Bylaws' }

export const SECTION_KINDS = ['chapter', 'policy', 'procedure', 'article', 'section', 'clause', 'text'] as const
export type SectionKind = (typeof SECTION_KINDS)[number]
export const SECTION_KIND_LABELS: Record<SectionKind, string> = { chapter: 'Chapter', policy: 'Policy', procedure: 'Procedure', article: 'Article', section: 'Section', clause: 'Clause', text: 'Text' }

export const MANUAL_VERSION_STATUSES = ['record', 'draft', 'adopted', 'withdrawn'] as const
export type ManualVersionStatus = (typeof MANUAL_VERSION_STATUSES)[number]
export const MANUAL_VERSION_ORIGINS = ['drafted', 'pasted', 'imported_file', 'record'] as const
export type ManualVersionOrigin = (typeof MANUAL_VERSION_ORIGINS)[number]
export const MANUAL_ORIGIN_NOTES: Record<ManualVersionOrigin, string | null> = {
  drafted: null,
  pasted: null,
  imported_file: 'Imported from the adopted file, check against the original',
  record: 'The text in effect, as transcribed',
}

// What a version is, read on a given day.
export const MANUAL_VERSION_STATES = ['record', 'draft', 'adopted', 'in_effect', 'superseded', 'withdrawn'] as const
export type ManualVersionState = (typeof MANUAL_VERSION_STATES)[number]
export const MANUAL_VERSION_STATE_LABELS: Record<ManualVersionState, string> = {
  record: 'Transcribing',
  draft: 'Draft',
  adopted: 'Adopted, not yet in effect',
  in_effect: 'In effect',
  superseded: 'Superseded',
  withdrawn: 'Withdrawn',
}

export const EFFECTIVE_RULES = ['on_adoption', 'on_date', 'on_condition'] as const
export type EffectiveRule = (typeof EFFECTIVE_RULES)[number]
export const EFFECTIVE_RULE_LABELS: Record<EffectiveRule, string> = { on_adoption: 'On adoption', on_date: 'On a date', on_condition: 'On a condition' }

export const EXCEPTION_APPROVERS = ['board', 'executive_committee', 'executive_director'] as const
export type ExceptionApprover = (typeof EXCEPTION_APPROVERS)[number]
export const EXCEPTION_APPROVER_LABELS: Record<ExceptionApprover, string> = { board: 'Board', executive_committee: 'Executive Committee', executive_director: 'Executive Director' }

export type EvidenceItem = { label: string; recordKind: string | null }

export type ParsedSection = {
  key: string
  parentKey: string | null
  kind: SectionKind
  ref: string | null
  title: string
  body: string
  owner: string | null
  implements: string[]
  evidence: EvidenceItem[]
  systemLink: string | null
}

export type ParsedManual = { title: string | null; kind: ManualKind; sections: ParsedSection[]; notes: string[] }

// ---------------------------------------------------------------- systems

// Where the work a procedure describes happens. The procedure links there,
// and that page links back to the procedure.
export const SYSTEM_LINKS = {
  purchasing: { label: 'Purchase requests', path: '/purchasing' },
  pos_drawers: { label: 'POS drawers and closeout', path: null },
  btowntix: { label: 'BTownTix', path: null },
  events: { label: 'Events and settlement', path: '/events' },
  rentals: { label: 'Rentals', path: '/rentals' },
  inspections: { label: 'Inspections and work orders', path: '/inspections' },
  incidents: { label: 'Incidents', path: '/incidents' },
  workforce: { label: 'Staff and volunteers', path: '/workforce' },
  grants: { label: 'Grants', path: '/grants' },
  sponsorships: { label: 'Sponsorships', path: '/rentals' },
  governing_documents: { label: 'Governing documents', path: '/governance/manuals' },
} as const satisfies Record<string, { label: string; path: string | null }>
export type SystemLink = keyof typeof SYSTEM_LINKS
export const isSystemLink = (v: unknown): v is SystemLink => typeof v === 'string' && Object.hasOwn(SYSTEM_LINKS, v)

// The plan's map (docs/architecture/BRE-Policy-Manual-Plan.md): which board
// policies each administrative procedure carries out, where its work
// happens, and the evidence it keeps. Applied when procedures are parsed;
// every value can be changed on the draft.
export const PROCEDURE_DEFAULTS: Record<string, { implements: string[]; systemLink: SystemLink | null; evidence: EvidenceItem[] }> = {
  'ADM-01': { implements: ['FIN-003', 'FIN-004'], systemLink: null, evidence: [{ label: 'Adopted budget', recordKind: 'adopted_budget' }, { label: 'Monthly close checklist and reconciliations', recordKind: 'financial_report' }] },
  'ADM-02': { implements: ['FIN-001', 'FIN-002'], systemLink: 'purchasing', evidence: [{ label: 'Purchase approval', recordKind: null }, { label: 'Invoice', recordKind: null }, { label: 'Quote comparison', recordKind: null }, { label: 'Waiver', recordKind: null }] },
  'ADM-03': { implements: ['FIN-006'], systemLink: 'pos_drawers', evidence: [{ label: 'Drawer count and variance log', recordKind: null }, { label: 'Deposit proof', recordKind: null }] },
  'ADM-04': { implements: ['FIN-006'], systemLink: 'btowntix', evidence: [{ label: 'Ticketing audit log', recordKind: null }, { label: 'Event reconciliation', recordKind: null }, { label: 'Gift card reconciliation', recordKind: null }] },
  'ADM-05': { implements: ['OPS-003', 'RISK-004'], systemLink: 'events', evidence: [{ label: 'Event budget', recordKind: null }, { label: 'Event settlement', recordKind: 'event_settlement' }] },
  'ADM-06': { implements: ['OPS-002', 'RISK-002'], systemLink: 'rentals', evidence: [{ label: 'Signed rental agreement', recordKind: null }, { label: 'Insurance certificate', recordKind: 'insurance_certificate' }, { label: 'Renter settlement', recordKind: null }] },
  'ADM-07': { implements: ['OPS-002', 'OPS-004'], systemLink: null, evidence: [{ label: 'City approval or consultation', recordKind: 'city_agreement' }, { label: 'Contractor documentation', recordKind: null }] },
  'ADM-08': { implements: ['RISK-003'], systemLink: 'inspections', evidence: [{ label: 'Inspection record', recordKind: null }, { label: 'Functional verification', recordKind: null }] },
  'ADM-09': { implements: ['RISK-004'], systemLink: 'incidents', evidence: [{ label: 'Incident report', recordKind: null }, { label: 'Footage hold', recordKind: null }] },
  'ADM-10': { implements: ['HR-001'], systemLink: 'workforce', evidence: [{ label: 'Offer and job description', recordKind: null }, { label: 'Timesheet approval', recordKind: null }, { label: 'Handbook acknowledgment', recordKind: 'personnel_policy' }] },
  'ADM-11': { implements: ['DEV-001'], systemLink: 'grants', evidence: [{ label: 'Grant agreement and register entry', recordKind: null }, { label: 'Donor acknowledgment', recordKind: null }] },
  'ADM-12': { implements: ['OPS-001', 'DEV-001'], systemLink: 'sponsorships', evidence: [{ label: 'Contract', recordKind: 'major_contract' }, { label: 'Sponsor agreement', recordKind: 'sponsor_agreement' }] },
  'ADM-13': { implements: ['TECH-001'], systemLink: null, evidence: [{ label: 'Restricted incident record', recordKind: null }] },
  'ADM-14': { implements: ['TECH-001'], systemLink: null, evidence: [{ label: 'Backup inventory', recordKind: null }, { label: 'Restoration test result', recordKind: null }] },
  'ADM-15': { implements: ['GOV-002', 'GOV-003'], systemLink: 'governing_documents', evidence: [{ label: 'Revision register', recordKind: null }, { label: 'Exception record', recordKind: null }] },
}

// --------------------------------------------------------------- parsing

const POLICY_ID = /^([A-Z]{2,5}-\d{2,3})\s+(\S.*)$/
const CHAPTER = /^(\d{1,2})\.\s+(\S.*)$/
const ARTICLE = /^article\s+([ivxlcdm]+|\d+)\b\s*[.:–—-]?\s*(.*)$/i
const BYLAWS_SECTION = /^(?:section|sec\.)\s+(\d+(?:\.\d+)*[a-z]?)\s*[.:–—-]?\s*(.*)$/i
const OWNER = /^Owner:\s*(.+?)\.\s+(\S[\s\S]*)$/

const lines = (text: string) =>
  text
    .replace(/\r\n?/g, '\n')
    .replace(/^﻿/, '')
    .split('\n')
    .map((l) => l.replace(/[ \t ]+$/g, '').replace(/^[ \t ]+/g, ''))

const join = (paragraphs: string[]) => paragraphs.filter((p) => p.length > 0).join('\n\n')

function uniqueKey(seen: Map<string, number>, key: string, notes: string[], label: string) {
  const n = (seen.get(key) ?? 0) + 1
  seen.set(key, n)
  if (n === 1) return key
  notes.push(`${label} appears ${n} times; the later one is kept as ${key}~${n}`)
  return `${key}~${n}`
}

const blank = (s: Partial<ParsedSection> & Pick<ParsedSection, 'key' | 'kind'>): ParsedSection => ({ parentKey: null, ref: null, title: '', body: '', owner: null, implements: [], evidence: [], systemLink: null, ...s })

// The Governance & Board Policy Manual's shape: a title line, chapters
// ("1. Document hierarchy …"), and policies by id ("GOV-001 Governing
// hierarchy"); every other line is a paragraph of the policy above it.
export function parsePolicyManual(text: string): ParsedManual {
  const notes: string[] = []
  const sections: ParsedSection[] = []
  const seen = new Map<string, number>()
  let title: string | null = null
  let chapter: ParsedSection | null = null
  let current: ParsedSection | null = null
  let paragraphs: string[] = []
  const flush = () => {
    if (current) current.body = join([current.body, ...paragraphs])
    paragraphs = []
  }
  let first = true
  for (const line of lines(text)) {
    if (!line) continue
    const policy = POLICY_ID.exec(line)
    const ch = CHAPTER.exec(line)
    if (first && !policy && !ch) {
      title = line
      first = false
      continue
    }
    first = false
    if (ch) {
      flush()
      chapter = blank({ key: uniqueKey(seen, `ch:${ch[1]}`, notes, `Chapter ${ch[1]}`), kind: 'chapter', ref: ch[1]!, title: ch[2]!.trim() })
      sections.push(chapter)
      current = chapter
    } else if (policy) {
      flush()
      current = blank({ key: uniqueKey(seen, policy[1]!, notes, policy[1]!), parentKey: chapter?.key ?? null, kind: 'policy', ref: policy[1]!, title: policy[2]!.trim() })
      sections.push(current)
    } else {
      if (!current) {
        current = blank({ key: 'preamble', kind: 'text' })
        sections.push(current)
      }
      paragraphs.push(line)
    }
  }
  flush()
  return { title, kind: 'board_policy', sections, notes }
}

// The administrative procedures: "ADM-01 Annual budget …", then
// "Procedure" and its text (an "Owner: …." first sentence becomes the
// owner), then "Minimum retained evidence" and its text. When every
// procedure keeps the same evidence sentence it becomes one clause for the
// whole manual.
export function parseProcedures(text: string): ParsedManual {
  const notes: string[] = []
  const seen = new Map<string, number>()
  const procedures: (ParsedSection & { evidenceText: string[] })[] = []
  let title: string | null = null
  let current: (ParsedSection & { evidenceText: string[] }) | null = null
  let mode: 'body' | 'evidence' = 'body'
  let body: string[] = []
  let preamble: string[] = []
  const flush = () => {
    if (!current) return
    const paras = [...body]
    const owner = paras.length > 0 ? OWNER.exec(paras[0]!) : null
    if (owner) {
      current.owner = owner[1]!.trim()
      paras[0] = owner[2]!.trim()
    }
    current.body = join(paras)
    body = []
  }
  let first = true
  for (const line of lines(text)) {
    if (!line) continue
    const proc = POLICY_ID.exec(line)
    if (first && !proc) {
      title = line
      first = false
      continue
    }
    first = false
    if (proc) {
      flush()
      const ref = proc[1]!
      const d = PROCEDURE_DEFAULTS[ref]
      current = { ...blank({ key: uniqueKey(seen, ref, notes, ref), kind: 'procedure', ref, title: proc[2]!.trim() }), implements: d ? [...d.implements] : [], systemLink: d?.systemLink ?? null, evidence: d ? d.evidence.map((e) => ({ ...e })) : [], evidenceText: [] }
      procedures.push(current)
      mode = 'body'
    } else if (/^procedure:?$/i.test(line)) {
      mode = 'body'
    } else if (/^minimum retained evidence:?$/i.test(line)) {
      mode = 'evidence'
    } else if (!current) {
      preamble.push(line)
    } else if (mode === 'evidence') {
      current.evidenceText.push(line)
    } else {
      body.push(line)
    }
  }
  flush()
  const sections: ParsedSection[] = []
  if (preamble.length > 0) sections.push(blank({ key: 'preamble', kind: 'text', body: join(preamble) }))
  const evidence = procedures.map((p) => join(p.evidenceText))
  const shared = procedures.length > 1 && evidence[0] !== '' && evidence.every((e) => e === evidence[0])
  if (shared) sections.push(blank({ key: 'clause:evidence', kind: 'clause', title: 'Minimum retained evidence', body: evidence[0]! }))
  for (const p of procedures) {
    const { evidenceText, ...section } = p
    if (!shared && evidenceText.length > 0) section.body = join([section.body, `Minimum retained evidence: ${join(evidenceText)}`])
    sections.push(section)
  }
  return { title, kind: 'ed_procedure', sections, notes }
}

const ROMAN: Record<string, number> = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 }
export function romanValue(s: string): number | null {
  if (/^\d+$/.test(s)) return Number(s)
  let total = 0
  const t = s.toLowerCase()
  for (let i = 0; i < t.length; i++) {
    const v = ROMAN[t[i]!]
    if (!v) return null
    const next = ROMAN[t[i + 1] ?? ''] ?? 0
    total += next > v ? -v : v
  }
  return total
}

// Bylaws: "ARTICLE III" (a title after it, or on the next line) and
// "Section 2. Number. The board …" under it.
export function parseBylaws(text: string): ParsedManual {
  const notes: string[] = []
  const seen = new Map<string, number>()
  const sections: ParsedSection[] = []
  let title: string | null = null
  let article: ParsedSection | null = null
  let current: ParsedSection | null = null
  let paragraphs: string[] = []
  let wantTitle = false
  const flush = () => {
    if (current) current.body = join([current.body, ...paragraphs])
    paragraphs = []
  }
  let first = true
  for (const line of lines(text)) {
    if (!line) continue
    const art = ARTICLE.exec(line)
    const sec = BYLAWS_SECTION.exec(line)
    if (first && !art && !sec) {
      title = line
      first = false
      continue
    }
    first = false
    if (art) {
      flush()
      const n = romanValue(art[1]!)
      const label = /^\d+$/.test(art[1]!) ? art[1]! : art[1]!.toUpperCase()
      article = blank({ key: uniqueKey(seen, `art:${n ?? label}`, notes, `Article ${label}`), kind: 'article', ref: `Article ${label}`, title: art[2]!.replace(/^[-.:\s]+/, '').trim() })
      sections.push(article)
      current = article
      wantTitle = article.title === ''
    } else if (sec) {
      flush()
      wantTitle = false
      const rest = sec[2]!.trim()
      const split = /^([^.]{1,80})\.\s+(\S[\s\S]*)$/.exec(rest)
      const titleText = split ? split[1]!.trim() : rest.length <= 80 ? rest.replace(/\.$/, '') : ''
      const bodyText = split ? split[2]!.trim() : rest.length <= 80 ? '' : rest
      const parent = article?.key ?? null
      current = blank({ key: uniqueKey(seen, `${parent ?? 'art:0'}:s:${sec[1]}`, notes, `Section ${sec[1]}`), parentKey: parent, kind: 'section', ref: `Section ${sec[1]}`, title: titleText, body: bodyText })
      sections.push(current)
    } else if (wantTitle && current && line.length <= 100 && !/[.;]$/.test(line)) {
      current.title = line
      wantTitle = false
    } else {
      wantTitle = false
      if (!current) {
        current = blank({ key: 'preamble', kind: 'text' })
        sections.push(current)
      }
      paragraphs.push(line)
    }
  }
  flush()
  return { title, kind: 'bylaws', sections, notes }
}

// A policy or other document without headings Hub knows: numbered
// paragraphs ("1.", "2.") become sections; otherwise one section.
export function parsePlainPolicy(text: string): ParsedManual {
  const asManual = parsePolicyManual(text)
  if (asManual.sections.some((s) => s.kind === 'policy' || s.kind === 'chapter')) {
    // Numbered chapters with no policy ids read as numbered sections.
    if (!asManual.sections.some((s) => s.kind === 'policy')) return { ...asManual, sections: asManual.sections.map((s) => (s.kind === 'chapter' ? { ...s, kind: 'section' as const, key: `s:${s.ref}` } : s)) }
    return asManual
  }
  const all = lines(text).filter(Boolean)
  const title = all.length > 1 ? all[0]! : null
  return { title, kind: 'board_policy', sections: [blank({ key: 'text', kind: 'text', body: join(title ? all.slice(1) : all) })], notes: [] }
}

export function parseManualText(kind: ManualKind, text: string): ParsedManual {
  if (kind === 'bylaws') return parseBylaws(text)
  if (kind === 'ed_procedure') return parseProcedures(text)
  return parsePlainPolicy(text)
}

// The content that identifies a version: every section in order with what
// it says. Its SHA-256 is the version's hash once adopted.
export type SectionContent = Pick<ParsedSection, 'key' | 'parentKey' | 'kind' | 'ref' | 'title' | 'body' | 'owner' | 'implements' | 'evidence' | 'systemLink'>
export function canonicalManual(title: string, sections: SectionContent[]): string {
  return JSON.stringify([title, sections.map((s) => [s.key, s.parentKey, s.kind, s.ref, s.title, s.body, s.owner, s.implements, s.evidence.map((e) => [e.label, e.recordKind]), s.systemLink])])
}
export async function manualContentHash(title: string, sections: SectionContent[]): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonicalManual(title, sections)))
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

// ------------------------------------------------------------- redlines

export type DiffOp = { op: 'eq' | 'ins' | 'del'; text: string }

const tokens = (s: string) => s.split(/(\s+)/).filter((t) => t.length > 0)

// Word by word. Very long texts compare as a whole replacement.
export function wordDiff(a: string, b: string): DiffOp[] {
  if (a === b) return a ? [{ op: 'eq', text: a }] : []
  const x = tokens(a)
  const y = tokens(b)
  const out: DiffOp[] = []
  const push = (op: DiffOp['op'], text: string) => {
    const last = out[out.length - 1]
    if (last && last.op === op) last.text += text
    else out.push({ op, text })
  }
  // Common start and end first.
  let start = 0
  while (start < x.length && start < y.length && x[start] === y[start]) start++
  let endX = x.length
  let endY = y.length
  while (endX > start && endY > start && x[endX - 1] === y[endY - 1]) {
    endX--
    endY--
  }
  for (let i = 0; i < start; i++) push('eq', x[i]!)
  const n = endX - start
  const m = endY - start
  if (n * m > 4_000_000) {
    for (let i = start; i < endX; i++) push('del', x[i]!)
    for (let j = start; j < endY; j++) push('ins', y[j]!)
  } else {
    const w = m + 1
    const lcs = new Uint32Array((n + 1) * w)
    for (let i = n - 1; i >= 0; i--)
      for (let j = m - 1; j >= 0; j--) lcs[i * w + j] = x[start + i] === y[start + j] ? lcs[(i + 1) * w + j + 1]! + 1 : Math.max(lcs[(i + 1) * w + j]!, lcs[i * w + j + 1]!)
    let i = 0
    let j = 0
    while (i < n && j < m) {
      if (x[start + i] === y[start + j]) {
        push('eq', x[start + i]!)
        i++
        j++
      } else if (lcs[(i + 1) * w + j]! >= lcs[i * w + j + 1]!) {
        push('del', x[start + i]!)
        i++
      } else {
        push('ins', y[start + j]!)
        j++
      }
    }
    while (i < n) push('del', x[start + i++]!)
    while (j < m) push('ins', y[start + j++]!)
  }
  for (let i = endX; i < x.length; i++) push('eq', x[i]!)
  return out
}

export type SectionChange = 'unchanged' | 'changed' | 'new' | 'removed'
export const SECTION_CHANGE_LABELS: Record<SectionChange, string> = { unchanged: 'Unchanged', changed: 'Changed', new: 'New', removed: 'Removed' }

export type RedlineSectionLike = Pick<ParsedSection, 'key' | 'kind' | 'ref' | 'title' | 'body'> & { parentKey?: string | null }
export type RedlineEntry = { key: string; kind: SectionKind; ref: string | null; change: SectionChange; title: DiffOp[]; body: DiffOp[]; titleText: string }
export type Redline = { entries: RedlineEntry[]; counts: Record<SectionChange, number> }

export const sectionChanged = (a: RedlineSectionLike, b: RedlineSectionLike) => a.title !== b.title || a.body !== b.body || (a.ref ?? null) !== (b.ref ?? null) || a.kind !== b.kind

// The draft's sections in order, each against the base's section with the
// same key; a section the base has and the draft doesn't is listed where it
// stood.
export function manualRedline(base: RedlineSectionLike[] | null, draft: RedlineSectionLike[]): Redline {
  const counts: Record<SectionChange, number> = { unchanged: 0, changed: 0, new: 0, removed: 0 }
  const before = new Map((base ?? []).map((s) => [s.key, s]))
  const inDraft = new Set(draft.map((s) => s.key))
  const entries: RedlineEntry[] = []
  const removedAfter = new Map<string | null, RedlineSectionLike[]>()
  let prev: string | null = null
  for (const s of base ?? []) {
    if (!inDraft.has(s.key)) removedAfter.set(prev, [...(removedAfter.get(prev) ?? []), s])
    else prev = s.key
  }
  const removed = (s: RedlineSectionLike): RedlineEntry => ({ key: s.key, kind: s.kind, ref: s.ref, change: 'removed', title: s.title ? [{ op: 'del', text: s.title }] : [], body: s.body ? [{ op: 'del', text: s.body }] : [], titleText: s.title })
  for (const r of removedAfter.get(null) ?? []) entries.push(removed(r))
  for (const s of draft) {
    const b = before.get(s.key)
    let change: SectionChange
    if (!base) change = 'new'
    else if (!b) change = 'new'
    else change = sectionChanged(b, s) ? 'changed' : 'unchanged'
    entries.push({
      key: s.key,
      kind: s.kind,
      ref: s.ref,
      change,
      title: change === 'new' ? (s.title ? [{ op: 'ins', text: s.title }] : []) : wordDiff(b!.title, s.title),
      body: change === 'new' ? (s.body ? [{ op: 'ins', text: s.body }] : []) : wordDiff(b!.body, s.body),
      titleText: s.title,
    })
    for (const r of removedAfter.get(s.key) ?? []) entries.push(removed(r))
  }
  for (const e of entries) counts[e.change]++
  return { entries, counts }
}

// "GOV-001", "ADM-12" and the like in a text, for links between sections.
export const SECTION_REF_PATTERN = /\b([A-Z]{2,5}-\d{2,3})\b/g
export function sectionRefsIn(text: string): string[] {
  return [...new Set([...text.matchAll(SECTION_REF_PATTERN)].map((m) => m[1]!))]
}

// A section's display heading: "GOV-013 Committees and limits",
// "Article III. Board of Directors", "Section 2. Number".
export function sectionHeading(s: Pick<ParsedSection, 'kind' | 'ref' | 'title'>): string {
  if (s.kind === 'chapter') return s.ref ? `${s.ref}. ${s.title}` : s.title
  if (s.kind === 'article' || s.kind === 'section') return [s.ref, s.title].filter(Boolean).join('. ')
  return [s.ref, s.title].filter(Boolean).join(' ')
}

// Search engines and shared links: a section's place in a URL.
export const sectionAnchor = (key: string) => key.replace(/[^A-Za-z0-9_-]/g, '-')

// ------------------------------------------------------------- views

export type ManualSectionView = {
  id: string
  key: string
  parentKey: string | null
  position: number
  kind: SectionKind
  ref: string | null
  title: string
  body: string
  owner: string | null
  adoptedOn: string | null
  effectiveOn: string | null
  revisedOn: string | null
  nextReviewOn: string | null
  predecessor: string | null
  implements: string[]
  evidence: EvidenceItem[]
  systemLink: SystemLink | null
  changesPolicy: boolean
  amendedByBoard: boolean
  updatedAt: string
  // Against the version this one is based on.
  change: SectionChange
  revisions: number
  comments: { open: number; total: number }
}

export type ManualCommentView = { id: string; sectionKey: string; person: { id: string; name: string }; body: string; channel: 'mybre' | 'hub'; createdAt: string; resolvedAt: string | null; resolvedBy: { id: string; name: string } | null; resolutionNote: string | null }
export type ManualShareView = { id: string; audience: 'board' | 'committee'; committee: { id: string; name: string } | null; sharedAt: string; sharedBy: string | null; sentAt: string | null; sentCount: number | null; endedAt: string | null }
export type ManualAgendaLinkView = {
  motionId: string
  effect: 'recommends' | 'adopts'
  excludedKeys: string[]
  meeting: { id: string; title: string; body: string; startsLocal: string; status: string }
  agendaItemId: string | null
  motionStatus: string
  motionTitle: string
  // Bylaws only: the written notice of intent the bylaws require.
  notice: BylawsNoticeView | null
}
export type BylawsNoticeView = {
  noticeDays: number
  // The first way it went out in time, or null.
  qualifying: { how: 'intent_notice' | 'packet' | 'meeting_notice'; at: string; days: number; label: string } | null
  // Recorded on the motion (the vote opens only with it).
  recorded: boolean
  // A packet or meeting notice that qualifies but isn't recorded yet.
  recordable: { how: 'packet' | 'meeting_notice'; at: string; label: string } | null
  warning: string | null
  changedSinceNotice: boolean
}

export type ManualVersionView = {
  id: string
  manualId: string
  manualTitle: string
  kind: ManualKind
  number: number
  status: ManualVersionStatus
  state: ManualVersionState
  stateLabel: string
  origin: ManualVersionOrigin
  originNote: string | null
  basedOn: { id: string; number: number } | null
  title: string
  note: string | null
  checkedAt: string | null
  contentHash: string | null
  adoptedOn: string | null
  adoptedBy: 'board' | 'executive_director' | 'record' | null
  resolution: { id: string; number: string } | null
  recommendedBy: { id: string; number: string } | null
  partial: boolean
  effectiveRule: EffectiveRule
  effectiveOn: string | null
  effectiveCondition: string | null
  conditionBylaws: { versionId: string; number: number; state: ManualVersionState } | null
  conditionRecord: { id: string; title: string } | null
  conditionMetOn: string | null
  sourcePolicyVersion: { id: string; version: number; documentId: string | null } | null
  sourceBylawsVersion: { id: string; label: string; documentId: string | null } | null
  bylawsVersion: { id: string; label: string } | null
  policyVersionId: string | null
  sections: ManualSectionView[]
  comments: ManualCommentView[]
  shares: ManualShareView[]
  agenda: ManualAgendaLinkView[]
  acknowledgments: { requested: number; acknowledged: number } | null
  createdAt: string
  updatedAt: string
  canEdit: boolean
}

export type ManualListItem = {
  id: string
  code: string | null
  kind: ManualKind
  title: string
  policyId: string | null
  implementsManual: { id: string; title: string } | null
  inEffect: { versionId: string; number: number; effectiveOn: string | null } | null
  pending: { versionId: string; number: number; adoptedOn: string | null; effectiveRule: EffectiveRule; effectiveOn: string | null } | null
  draft: { versionId: string; number: number; updatedAt: string; shared: boolean; openComments: number } | null
  record: { versionId: string; number: number; sections: number } | null
  reviewDueOn: string | null
  reviewOverdue: boolean
  openExceptions: number
  procedureReviews: number
}

export type ManualExceptionView = { id: string; sectionKey: string | null; approvedBy: ExceptionApprover; authority: string; reason: string; scope: string; startsOn: string; expiresOn: string | null; reviewOn: string | null; resolution: { id: string; number: string } | null; recordedBy: string | null; createdAt: string; endedOn: string | null; endReason: string | null; expired: boolean; reviewDue: boolean }
export type ManualReviewView = { id: string; versionId: string | null; reviewedOn: string; outcome: 'no_change' | 'revision_needed'; note: string | null; committee: { id: string; name: string } | null; sectionsDue: string[]; recordedBy: string | null }
// A procedure to look at again because a policy it carries out changed
// (ADM-15: within 30 days of the board's change).
export type ProcedureReviewFlag = { procedureKey: string; procedureRef: string | null; procedureTitle: string; policyKey: string; changedOn: string; dueOn: string; manualId: string; versionNumber: number }

export type ManualDetail = {
  manual: ManualListItem & { reviewEveryMonths: number | null; retiredAt: string | null }
  versions: { id: string; number: number; status: ManualVersionStatus; state: ManualVersionState; stateLabel: string; origin: ManualVersionOrigin; adoptedOn: string | null; effectiveOn: string | null; resolution: { id: string; number: string } | null; createdAt: string }[]
  exceptions: ManualExceptionView[]
  reviews: ManualReviewView[]
  procedureFlags: ProcedureReviewFlag[]
  // For a board policy manual: procedures that carry out its sections.
  procedures: { manualId: string; key: string; ref: string | null; title: string; implements: string[] }[]
  sectionsDue: string[]
}

// What a frozen packet carries on the item that puts a manual before a
// meeting (only there): the text as it stood, and its redline against the
// version in effect. The board book prints both.
export type BoardBookManual = {
  versionId: string
  manualTitle: string
  kind: ManualKind
  number: number
  // ADR 0073: 'informs', ED procedures for information (no motion).
  effect: 'recommends' | 'adopts' | 'informs'
  contentHash: string
  excludedKeys: string[]
  basedOnNumber: number | null
  sections: { key: string; parentKey: string | null; kind: SectionKind; ref: string | null; title: string; body: string; change: SectionChange }[]
  // ADR 0073: a removed section also names the section it followed
  // (`after`, null at the start) and its parent.
  redline: { key: string; kind: SectionKind; ref: string | null; change: SectionChange; title: DiffOp[]; body: DiffOp[]; after?: string | null; parentKey?: string | null }[] | null
}

// --------------------------------------------------------- the load script

// The drafts a load script puts in: parsed text with fixed ids, the edits
// to apply after it (recorded as the first edits), and the bylaws shell.
export type LoadSection = ParsedSection
export type LoadEdit = { key: string; find: string; replace: string; note: string }
export type LoadManual = {
  code: string
  kind: ManualKind
  title: string
  // The record version first (bylaws: the text in effect, empty when Hub has none).
  record?: { bylawsVersionId: string | null; adoptedOn: string; effectiveOn: string; sections: LoadSection[] }
  draft: { sections: LoadSection[]; edits: LoadEdit[]; note: string | null }
  implementsCode?: string
}

// One statement per line: a line break inside text is written as a marker
// that replace() turns into char(10). (A chain of `|| char(10) ||` per line
// break hits D1's expression depth limit of 100 on a long section.)
const NL = String.fromCharCode(10)
const CR = String.fromCharCode(13)
const NL_MARK = '{{NL}}'
const q = (v: string | null | undefined) => {
  if (v === null || v === undefined) return 'NULL'
  if (v.includes(NL_MARK)) throw new Error(`Text contains ${NL_MARK}`)
  const s = v.split(CR).join('').split("'").join("''")
  return s.includes(NL) ? `replace('${s.split(NL).join(NL_MARK)}', '${NL_MARK}', char(10))` : `'${s}'`
}
const n = (v: number) => String(Math.trunc(v))

export const loadIds = (code: string) => ({
  manual: `man_${code}`,
  record: `mnv_${code}_1`,
  draft: (hasRecord: boolean) => `mnv_${code}_${hasRecord ? 2 : 1}`,
  section: (version: string, i: number) => `mns_${version.slice(4)}_${String(i + 1).padStart(3, '0')}`,
  revision: (version: string, i: number, suffix = 'i') => `msr_${version.slice(4)}_${String(i + 1).padStart(3, '0')}${suffix}`,
})

// Guarded SQL: a manual (by its code) is made only when none exists; each
// section only while its version is exactly as this file made it (its
// updated_at is the run's time, which every edit in Hub changes) and the
// section isn't there; an edit only while the section still reads as
// imported. Running the file twice changes nothing; it never touches what
// the owner has edited. Nothing is adopted, shared or sent.
export function manualLoadSql(input: { runAt: string; manuals: LoadManual[]; source: string }): string {
  const at = input.runAt
  const out: string[] = []
  for (const m of input.manuals) {
    const ids = loadIds(m.code)
    const draftId = ids.draft(!!m.record)
    const implementsId = m.implementsCode ? loadIds(m.implementsCode).manual : null
    out.push(`-- ${m.title} (${m.code})`)
    out.push(
      `INSERT INTO manual (id, code, kind, title, policy_id, implements_manual_id, review_every_months, position, created_by_person_id, created_at, updated_at) SELECT ${q(ids.manual)}, ${q(m.code)}, ${q(m.kind)}, ${q(m.title)}, NULL, ${implementsId ? `(SELECT id FROM manual WHERE id = ${q(implementsId)})` : 'NULL'}, ${m.kind === 'bylaws' ? 'NULL' : '12'}, ${m.kind === 'bylaws' ? 10 : m.kind === 'board_policy' ? 20 : 30}, NULL, ${q(at)}, ${q(at)} WHERE NOT EXISTS (SELECT 1 FROM manual WHERE code = ${q(m.code)});`,
    )
    const fresh = (versionId: string) => `(SELECT updated_at FROM manual_version WHERE id = ${q(versionId)}) = ${q(at)}`
    const mine = `(SELECT created_at FROM manual WHERE id = ${q(ids.manual)}) = ${q(at)}`
    const versionSql = (id: string, number: number, status: string, origin: string, basedOn: string | null, extra: { bylaws?: string | null; adoptedOn?: string; effectiveOn?: string; note?: string | null }) =>
      `INSERT INTO manual_version (id, manual_id, number, status, origin, based_on_version_id, title, note, source_bylaws_version_id, adopted_on, adopted_by, effective_rule, effective_on, created_by_person_id, created_at, updated_at) SELECT ${q(id)}, ${q(ids.manual)}, ${n(number)}, ${q(status)}, ${q(origin)}, ${q(basedOn)}, ${q(m.title)}, ${q(extra.note ?? null)}, ${extra.bylaws === undefined || extra.bylaws === null ? 'NULL' : `(SELECT id FROM bylaws_version WHERE id = ${q(extra.bylaws)})`}, ${q(extra.adoptedOn ?? null)}, ${status === 'record' ? q('record') : 'NULL'}, ${extra.effectiveOn ? q('on_date') : q('on_adoption')}, ${q(extra.effectiveOn ?? null)}, NULL, ${q(at)}, ${q(at)} WHERE ${mine} AND NOT EXISTS (SELECT 1 FROM manual_version WHERE manual_id = ${q(ids.manual)});`
    const sectionsSql = (versionId: string, sections: LoadSection[]) => {
      sections.forEach((s, i) => {
        const sid = ids.section(versionId, i)
        out.push(
          `INSERT INTO manual_section (id, version_id, key, parent_key, position, kind, ref, title, body, owner, implements_json, evidence_json, system_link, updated_by_person_id, created_at, updated_at) SELECT ${q(sid)}, ${q(versionId)}, ${q(s.key)}, ${q(s.parentKey)}, ${n(i + 1)}, ${q(s.kind)}, ${q(s.ref)}, ${q(s.title)}, ${q(s.body)}, ${q(s.owner)}, ${q(JSON.stringify(s.implements))}, ${q(JSON.stringify(s.evidence))}, ${q(s.systemLink)}, NULL, ${q(at)}, ${q(at)} WHERE ${fresh(versionId)} AND NOT EXISTS (SELECT 1 FROM manual_section WHERE version_id = ${q(versionId)} AND key = ${q(s.key)});`,
        )
        out.push(
          `INSERT INTO manual_section_revision (id, version_id, section_key, action, ref, title, body, note, by_person_id, created_at) SELECT ${q(ids.revision(versionId, i))}, ${q(versionId)}, ${q(s.key)}, 'imported', ${q(s.ref)}, ${q(s.title)}, ${q(s.body)}, ${q(input.source)}, NULL, ${q(at)} WHERE ${fresh(versionId)} AND NOT EXISTS (SELECT 1 FROM manual_section_revision WHERE id = ${q(ids.revision(versionId, i))});`,
        )
      })
    }
    if (m.record) {
      out.push(versionSql(ids.record, 1, 'record', 'record', null, { bylaws: m.record.bylawsVersionId, adoptedOn: m.record.adoptedOn, effectiveOn: m.record.effectiveOn }))
      sectionsSql(ids.record, m.record.sections)
      // The draft follows the record version.
      out.push(versionSql(draftId, 2, 'draft', 'drafted', ids.record, { note: m.draft.note }).replace(`AND NOT EXISTS (SELECT 1 FROM manual_version WHERE manual_id = ${q(ids.manual)})`, `AND NOT EXISTS (SELECT 1 FROM manual_version WHERE manual_id = ${q(ids.manual)} AND number = 2)`))
    } else {
      out.push(versionSql(draftId, 1, 'draft', 'pasted', null, { note: m.draft.note }))
    }
    sectionsSql(draftId, m.draft.sections)
    // The first edits, each once, only on the text as imported.
    m.draft.edits.forEach((e, j) => {
      const i = m.draft.sections.findIndex((s) => s.key === e.key)
      if (i < 0) throw new Error(`No section ${e.key} to edit`)
      const s = m.draft.sections[i]!
      if (!s.body.includes(e.find)) throw new Error(`${e.key} doesn't contain the text to replace`)
      const sid = ids.section(draftId, i)
      const body = s.body.replace(e.find, e.replace)
      const revId = ids.revision(draftId, i, `e${j + 1}`)
      out.push(`INSERT INTO manual_section_revision (id, version_id, section_key, action, ref, title, body, note, by_person_id, created_at) SELECT ${q(revId)}, ${q(draftId)}, ${q(s.key)}, 'edited', ${q(s.ref)}, ${q(s.title)}, ${q(body)}, ${q(e.note)}, NULL, ${q(at)} WHERE ${fresh(draftId)} AND (SELECT body FROM manual_section WHERE id = ${q(sid)}) = ${q(s.body)} AND NOT EXISTS (SELECT 1 FROM manual_section_revision WHERE id = ${q(revId)});`)
      out.push(`UPDATE manual_section SET body = ${q(body)} WHERE id = ${q(sid)} AND body = ${q(s.body)} AND ${fresh(draftId)} AND EXISTS (SELECT 1 FROM manual_section_revision WHERE id = ${q(revId)});`)
    })
    out.push(
      `INSERT INTO activity (id, actor_person_id, action, object_type, object_id, request_id, detail_json) SELECT ${q(`act_load_${m.code}`)}, NULL, 'governance.manual.loaded', 'manual', ${q(ids.manual)}, NULL, ${q(JSON.stringify({ source: input.source, sections: m.draft.sections.length, recordSections: m.record?.sections.length ?? null, edits: m.draft.edits.length }))} WHERE ${mine} AND NOT EXISTS (SELECT 1 FROM activity WHERE id = ${q(`act_load_${m.code}`)});`,
    )
  }
  return out.join('\n') + '\n'
}

// ------------------------------------------------------ a scripted rewrite

// The sections as the load script left them: the parsed text with its
// first edits applied.
export function applyLoadEdits(sections: LoadSection[], edits: LoadEdit[]): LoadSection[] {
  const out = sections.map((s) => ({ ...s }))
  for (const e of edits) {
    const s = out.find((x) => x.key === e.key)
    if (!s) throw new Error(`No section ${e.key} to edit`)
    if (!s.body.includes(e.find)) throw new Error(`${e.key} doesn't contain the text to replace`)
    s.body = s.body.replace(e.find, e.replace)
  }
  return out
}

export type RewriteManual = {
  code: string
  // The draft the load script made, and its sections as loaded (edits applied).
  versionId: string
  loaded: LoadSection[]
  rewritten: LoadSection[]
  // Appended to the revision note of a section's rewrite (open items).
  sectionNotes?: Record<string, string>
  // Set on the draft only while it has no note; with versionNoteFrom, only
  // while the note reads exactly that, which it replaces.
  versionNote?: string | null
  versionNoteFrom?: string
  // A later pass: the text a section may read instead of `loaded` (the
  // owner's own edit, as read just before the run). The section is written
  // while it reads as either one and not already as rewritten.
  alsoFrom?: Record<string, { title: string; body: string; owner: string | null }>
  // Sections removed since `loaded`: added back as rewritten (a revision
  // 'added' and a section_added activity row, as Hub's add does) while the
  // key is absent, its latest revision is the removal and its old position
  // is free.
  restore?: string[]
}

// A rewrite of loaded drafts, saved as an edit of each section, the way a
// save in Hub is (a revision 'edited', the section, an activity row, the
// draft's updated_at), with no actor. Guarded: a section changes only while
// its title, body and owner still read exactly as loaded, so a section the
// owner has edited or removed is left alone (unless a later pass names the
// owner's text in alsoFrom, or the removed key in restore); each save once
// (a fixed revision id per pass). Running the file again writes nothing.
// Sections the rewrite leaves unchanged get no statement.
export function manualRewriteSql(input: { runAt: string; note: string; manuals: RewriteManual[]; pass?: string }): { sql: string; sections: { code: string; key: string }[] } {
  const at = input.runAt
  // Each pass has its own fixed ids: w1 the first rewrite, w2 the next.
  const pass = input.pass ?? 'w1'
  if (!/^w\d+$/.test(pass)) throw new Error(`Bad pass ${pass}`)
  const out: string[] = []
  const sections: { code: string; key: string }[] = []
  for (const m of input.manuals) {
    const v = m.versionId
    const rewritten = new Map<string, LoadSection>()
    for (const s of m.rewritten) {
      if (rewritten.has(s.key)) throw new Error(`${s.key} appears twice in the rewrite`)
      if (!m.loaded.some((l) => l.key === s.key)) throw new Error(`${s.key} is not in the loaded ${m.code}`)
      rewritten.set(s.key, s)
    }
    for (const l of m.loaded) if (!rewritten.has(l.key)) throw new Error(`${l.key} is missing from the rewrite of ${m.code}`)
    out.push(`-- ${m.code}: the rewrite, section by section, only where the text is still as loaded`)
    m.loaded.forEach((l, i) => {
      const r = rewritten.get(l.key)!
      const revId = loadIds(m.code).revision(v, i, pass)
      const actId = `act_rewrite_${revId.slice(4)}`
      const saved = `EXISTS (SELECT 1 FROM manual_section_revision WHERE id = ${q(revId)} AND created_at = ${q(at)})`
      if (m.restore?.includes(l.key)) {
        sections.push({ code: m.code, key: l.key })
        const free = `NOT EXISTS (SELECT 1 FROM manual_section WHERE version_id = ${q(v)} AND (key = ${q(l.key)} OR position = ${n(i + 1)}))`
        const removed = `(SELECT action FROM manual_section_revision WHERE version_id = ${q(v)} AND section_key = ${q(l.key)} ORDER BY created_at DESC, id DESC LIMIT 1) = 'removed'`
        out.push(
          `INSERT INTO manual_section_revision (id, version_id, section_key, action, ref, title, body, note, by_person_id, created_at) SELECT ${q(revId)}, ${q(v)}, ${q(l.key)}, 'added', ${q(r.ref)}, ${q(r.title)}, ${q(r.body)}, ${q(input.note)}, NULL, ${q(at)} WHERE EXISTS (SELECT 1 FROM manual_version WHERE id = ${q(v)} AND status = 'draft') AND ${free} AND ${removed} AND NOT EXISTS (SELECT 1 FROM manual_section_revision WHERE id = ${q(revId)});`,
        )
        out.push(
          `INSERT INTO manual_section (id, version_id, key, parent_key, position, kind, ref, title, body, owner, implements_json, evidence_json, system_link, updated_by_person_id, created_at, updated_at) SELECT ${q(`${loadIds(m.code).section(v, i)}${pass}`)}, ${q(v)}, ${q(l.key)}, ${q(r.parentKey)}, ${n(i + 1)}, ${q(r.kind)}, ${q(r.ref)}, ${q(r.title)}, ${q(r.body)}, ${q(r.owner)}, ${q(JSON.stringify(r.implements))}, ${q(JSON.stringify(r.evidence))}, ${q(r.systemLink)}, NULL, ${q(at)}, ${q(at)} WHERE ${free} AND ${saved};`,
        )
        out.push(
          `INSERT INTO activity (id, actor_person_id, action, object_type, object_id, request_id, detail_json) SELECT ${q(actId)}, NULL, 'governance.manual.section_added', 'manual', (SELECT manual_id FROM manual_version WHERE id = ${q(v)}), NULL, ${q(JSON.stringify({ versionId: v, key: l.key, source: input.note }))} WHERE ${saved} AND NOT EXISTS (SELECT 1 FROM activity WHERE id = ${q(actId)});`,
        )
        return
      }
      const also = m.alsoFrom?.[l.key]
      const bases = also ? [l, also] : [l]
      const same = (a: { title: string; body: string; owner: string | null }) => r.title === a.title && r.body === a.body && r.owner === a.owner
      if (bases.every(same)) return
      sections.push({ code: m.code, key: l.key })
      const reads = (a: { title: string; body: string; owner: string | null }) => `(title = ${q(a.title)} AND body = ${q(a.body)} AND owner IS ${q(a.owner)})`
      const asLoaded = also
        ? `version_id = ${q(v)} AND key = ${q(l.key)} AND (${reads(l)} OR ${reads(also)}) AND NOT ${reads(r)}`
        : `version_id = ${q(v)} AND key = ${q(l.key)} AND title = ${q(l.title)} AND body = ${q(l.body)} AND owner IS ${q(l.owner)}`
      const extra = m.sectionNotes?.[l.key]
      const note = extra ? `${input.note}${NL}${NL}${extra}` : input.note
      const fields = [bases.some((b) => r.title !== b.title) && 'title', bases.some((b) => r.body !== b.body) && 'body', bases.some((b) => r.owner !== b.owner) && 'owner'].filter(Boolean)
      out.push(
        `INSERT INTO manual_section_revision (id, version_id, section_key, action, ref, title, body, note, by_person_id, created_at) SELECT ${q(revId)}, ${q(v)}, ${q(l.key)}, 'edited', ${q(l.ref)}, ${q(r.title)}, ${q(r.body)}, ${q(note)}, NULL, ${q(at)} WHERE EXISTS (SELECT 1 FROM manual_version WHERE id = ${q(v)} AND status = 'draft') AND EXISTS (SELECT 1 FROM manual_section WHERE ${asLoaded}) AND NOT EXISTS (SELECT 1 FROM manual_section_revision WHERE id = ${q(revId)});`,
      )
      out.push(
        `UPDATE manual_section SET title = ${q(r.title)}, body = ${q(r.body)}, owner = ${q(r.owner)}, updated_by_person_id = NULL, updated_at = ${q(at)} WHERE ${asLoaded} AND EXISTS (SELECT 1 FROM manual_section_revision WHERE id = ${q(revId)} AND created_at = ${q(at)});`,
      )
      out.push(
        `INSERT INTO activity (id, actor_person_id, action, object_type, object_id, request_id, detail_json) SELECT ${q(actId)}, NULL, 'governance.manual.section_edited', 'manual', (SELECT manual_id FROM manual_version WHERE id = ${q(v)}), NULL, ${q(JSON.stringify({ versionId: v, key: l.key, fields, source: input.note }))} WHERE EXISTS (SELECT 1 FROM manual_section_revision WHERE id = ${q(revId)} AND created_at = ${q(at)}) AND NOT EXISTS (SELECT 1 FROM activity WHERE id = ${q(actId)});`,
      )
    })
    out.push(
      `UPDATE manual_version SET updated_at = ${q(at)}, note = CASE WHEN ${m.versionNoteFrom === undefined ? 'note IS NULL' : `note = ${q(m.versionNoteFrom)}`} THEN ${q(m.versionNote ?? null)} ELSE note END WHERE id = ${q(v)} AND status = 'draft' AND updated_at <> ${q(at)} AND EXISTS (SELECT 1 FROM manual_section_revision WHERE version_id = ${q(v)} AND action IN ('edited', 'added') AND by_person_id IS NULL AND created_at = ${q(at)});`,
    )
  }
  return { sql: out.join('\n') + '\n', sections }
}
